import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DATE_FORMAT_MMMM_YYYY, LOCALE_CA, LOCALE_ES } from '@app/@core/constants/common.constants';
import { TransformNumberPipe } from '@app/@shared/pipes/transform-number.pipe';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import { TranslateService } from '@ngx-translate/core';
import FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment, { Moment } from 'moment';
import { ControlPanelStore } from '../control-panel-service/control-panel.store';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DatepickerUtilsService } from '@app/@shared/services/utils/datepicker-utils.service';
import { ReqIncentiveDetailsDTOInfo, ReqIncentiveDetailsDTOResponse } from '../models/control-panel.model';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { dataPrint } from '@app/activity/model/activity-model';
import { excelDataPenalty } from '@app/@shared/services/print/excel-models';
import {
  COMI_VENTAS,
  COMI_SEGURO_VIDA,
  COMI_SEGURO_PAGO,
  AMOUNT_COLLECTED,
  AMOUNT_PAID,
  PENALTY_YES,
  PENALTY_NO,
  RAPPEL_SIGLAS,
  RAPPEL,
} from './penalty-detail.constants';
import { MatSelectChange } from '@angular/material/select';

@UntilDestroy()
@Component({
  selector: 'app-penalty-detail',
  templateUrl: './penalty-detail.component.html',
  styleUrls: ['./penalty-detail.component.scss'],
})
export class PenaltyDetailComponent implements OnInit {
  readonly pageSize: number = 100;
  maxDate: Date = new Date();
  maxFinalDate: Date;
  minDate: Date;
  data: ReqIncentiveDetailsDTOResponse = null;
  establishmentsList: CommonDataDTO[];
  private isEstablishmentsLoading: boolean = false;
  private isCheckAllEstablishment: boolean = false;
  penaltyForm: FormGroup;
  pageIndex: number = 1;
  totalPage: number;
  establishmentName: string;

  public head = [
    'penalty.excel.codBenef',
    'penalty.excel.beneficiary',
    'penalty.excel.contractNumber',
    'penalty.excel.clientName',
    'penalty.excel.product',
    'penalty.excel.date',
    'penalty.excel.amountPenalty',
    'penalty.excel.reason',
  ];

  constructor(
    private formBuilder: FormBuilder,
    private excelService: ExcelService,
    private combosStore: CombosStore,
    private customValidators: CustomValidatorsService,
    private controlPanelStore: ControlPanelStore,
    private transformNumberPipe: TransformNumberPipe,
    private translateService: TranslateService,
    private datepickerUtils: DatepickerUtilsService,
    private loaderGenericService: LoaderGenericService
  ) {}

  ngOnInit(): void {
    this.penaltyForm = this.formBuilder.group(
      {
        initialDate: ['', [Validators.required, this.customValidators.checkIsFutureDate]],
        finalDate: [{ value: '', disabled: true }, [Validators.required, this.customValidators.checkIsFutureDate]],
        establishment: ['', Validators.required],
      },
      {
        validators: [
          this.customValidators.isDateValid('initialDate', 'finalDate', 'dateInvalid'),
          this.customValidators.isMoreThanAYearApart('initialDate', 'finalDate', 'dateYearError'),
        ],
      }
    );

    this.translateService.currentLang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan();
    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe((event) =>
        event.lang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan()
      );

    this.penaltyForm.controls.initialDate.statusChanges.subscribe((value) => {
      const initialDateValue = this.penaltyForm.controls.initialDate.value;
      if (value === 'VALID' || initialDateValue) {
        this.minDate = initialDateValue.toDate();
        this.maxFinalDate = moment(initialDateValue).add(11, 'months').endOf('month').toDate();
        if (this.penaltyForm.controls.finalDate.disabled) this.penaltyForm.controls.finalDate.enable();
      } else {
        this.minDate = undefined;
        this.maxFinalDate = this.maxDate;
      }
    });

    this.loadEstablishmentCombo();
  }

  obteinCurrentPage(page: number) {
    this.pageIndex = page;
    this.searchPenalty();
  }

  searchPenalty(): void {
    this.data = {
      results: [],
      numPages: 0,
      pageSize: 0,
      pageIndex: 0,
      establishmentName: '',
    };
    this.controlPanelStore
      .getPenaltyDetailReport(this.penaltyForm, this.pageIndex, this.pageSize)
      .then((data) => {
        if (null != data) {
          this.data = data;
          this.data.establishmentName = this.establishmentName;
          this.pageIndex = this.data.pageIndex;
          this.totalPage = this.data.numPages;
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  setCheckAll(event: { id: string; value: boolean }) {
    const { id, value } = event;
    switch (id) {
      case 'establishmentSelect':
        this.isCheckAllEstablishment = value;
        break;
      default:
        console.error('setCheckAll - NOT FOUND', id);
    }
  }

  private loadEstablishmentCombo = async () => {
    this.isEstablishmentsLoading = true;
    this.loaderGenericService.showSpinner();
    this.combosStore
      .getEstablishments()
      .then(
        (result) => {
          if (result?.length > 0) {
            this.establishmentsList = result;
            this.isCheckAllEstablishment = true;
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error))
      .finally(() => {
        this.isEstablishmentsLoading = false;
        this.isLoadersCompleted() ? this.loaderGenericService.stopSpinner() : null;
      });
  };

  /* HANDLERS */

  chosenMonthHandler(normalizedMonth: Moment, datepicker: MatDatepicker<Moment>, input: any) {
    const inputForm = typeof input === 'string' ? input : input?.getAttribute('formControlName');
    const control: AbstractControl = this.penaltyForm.controls[inputForm];
    const ctrlValue = this.penaltyForm.controls[inputForm].value;
    if (control.pristine) control.markAsDirty();
    if (ctrlValue) {
      ctrlValue.month(normalizedMonth.month());
      control.setValue(ctrlValue);
    } else {
      control.setValue(
        moment('1/' + (normalizedMonth.month() + 1).toString() + '/' + normalizedMonth.year().toString(), 'DD-MM-YYYY')
      );
    }
    datepicker.close();
  }

  chosenYearHandler(normalizedYear: Moment, input: any) {
    const inputForm = typeof input === 'string' ? input : input?.getAttribute('formControlName');
    const control: AbstractControl = this.penaltyForm.controls[inputForm];
    const ctrlValue = this.penaltyForm.controls[inputForm].value;
    if (ctrlValue) {
      ctrlValue.year(normalizedYear.year());
      control.setValue(ctrlValue);
    } else {
      control.setValue(moment('1/1/' + normalizedYear.year().toString(), 'DD-MM-YYYY'));
    }
  }

  private isLoadersCompleted = () => !this.isEstablishmentsLoading;

  translateResult(): ReqIncentiveDetailsDTOInfo[] {
    const resultCopyTranslate: ReqIncentiveDetailsDTOInfo[] = [];
    let reasonType;

    for (const item of this.data.results) {
      const itemCopy = { ...item };

      if (itemCopy.sign === AMOUNT_PAID) {
        reasonType = this.translateService.instant('penalty.excel.payment');
      } else if (itemCopy.sign === AMOUNT_COLLECTED) {
        reasonType = this.translateService.instant('penalty.excel.cashing');
        if (itemCopy.penalty === PENALTY_YES) {
          reasonType = reasonType + ' ' + this.translateService.instant('penalty.excel.cancel');
        }
        if (itemCopy.penalty === PENALTY_NO) {
          reasonType = reasonType + ' ' + this.translateService.instant('penalty.excel.return');
        }
      }

      if (itemCopy.reason === COMI_VENTAS) {
        itemCopy.reason = reasonType + ' ' + this.translateService.instant('penalty.excel.sales');
      } else if (itemCopy.reason === COMI_SEGURO_VIDA) {
        itemCopy.reason = reasonType + ' ' + this.translateService.instant('penalty.excel.sure.life');
      } else if (itemCopy.reason === COMI_SEGURO_PAGO) {
        itemCopy.reason = reasonType + ' ' + this.translateService.instant('penalty.excel.sure.payment');
      } else if (itemCopy.reason === RAPPEL_SIGLAS) {
        itemCopy.reason = reasonType + ' ' + RAPPEL;
      } else {
        itemCopy.reason = '';
      }

      if (!itemCopy.autDate) {
        itemCopy.autDate = '';
      }

      if (itemCopy.amountPenalty) {
        itemCopy.amountPenalty = item.sign + ' ' + this.transformNumberPipe.transform(item.amountPenalty);
      }

      resultCopyTranslate.push(itemCopy);
    }

    return resultCopyTranslate;
  }

  createPdf() {
    try {
      let doc = new jsPDF({
        orientation: 'landscape',
        format: [296, 209],
      });

      const title = this.translateService.instant('penalty.detail.title');
      const date = moment().locale('es');
      const filename = this.translateService.instant('penalty.detail.title.dashboard') + date.format('DDMMYYYY');

      doc.setProperties({
        title: title,
      });

      let dataColumns: string[] = [
        'codBeneficiary',
        'beneficiary',
        'contractId',
        'holderName',
        'productDesc',
        'autDate',
        'amountPenalty',
        'reason',
      ];

      let dataPdf: ReqIncentiveDetailsDTOResponse[] = [];
      const copyData = this.translateResult();

      /* BODY */
      copyData.forEach((item) => {
        const dataPdfOrder = JSON.stringify(item, dataColumns);
        const ObjectDataPdf = JSON.parse(dataPdfOrder);
        dataPdf.push(ObjectDataPdf);
      });

      autoTable(doc, {
        startY: 55,
        head: [this.head.map((header: any) => this.translateService.instant(header))],
        headStyles: { fillColor: '#969696' },
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        margin: { top: 55 },
        body: dataPdf.map(Object.values),
        didDrawPage: (data) => {
          doc.setFontSize(24);
          doc.setTextColor('1E90FF');
          doc.text(this.translateService.instant('excel.dashboard.title'), 160, 25);
          doc.setTextColor('#000000');
          doc.setFontSize(16);
          doc.text(this.translateService.instant('penalty.detail.title'), 14, 52);
          doc.setFontSize(12);
          doc.text(
            'De ' + this.getInitialDate(DATE_FORMAT_MMMM_YYYY) + ' a ' + this.getFinalDate(DATE_FORMAT_MMMM_YYYY),
            110,
            37
          );
          doc.text(this.translateService.instant('penalty.excel.page') + ' ' + data.pageNumber, 220, 45);

          doc.text(
            this.translateService.instant('penalty.excel.date') + ': ' + date.format('DD-MM-YYYY HH:mm:ss'),
            220,
            37
          );

          var img = new Image();
          img.src = 'assets/img/logo.PNG';
          doc.addImage(img, 'png', 3, 10, 80, 30);
        },
      });

      var blob = doc.output('blob');
      FileSaver.saveAs(blob, filename);
      window.open(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  }

  createExcel() {
    let allDataExcel: dataPrint = {
      headers: this.head.map((header: string) => this.translateService.instant(header)),
      data: this.translateResult(),
    };
    const excelAllData: excelDataPenalty = {
      initialDate: this.getInitialDate(DATE_FORMAT_MMMM_YYYY),
      finalDate: this.getFinalDate(DATE_FORMAT_MMMM_YYYY),
      data: allDataExcel,
    };
    this.excelService.exporExcelPenaltyDetail(excelAllData);
  }

  private getInitialDate(format: string) {
    return this.penaltyForm
      ?.get('initialDate')
      ?.value.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }

  private getFinalDate(format: string) {
    return this.penaltyForm
      ?.get('finalDate')
      ?.value.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }

  getEstablishmentName(e: MatSelectChange) {
    this.establishmentName = e.source.triggerValue;
  }
}
